import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, ModalService, PatientService, ToastService, UserService } from '../../services';
import { take } from 'rxjs/operators';

interface NoteItem {
  content: string;
  author: string;
  date: string;
  rawDate?: string;
}

@Component({
  selector: 'app-pop-up-add-note',
  templateUrl: './pop-up-add-note.component.html',
  styleUrls: ['./pop-up-add-note.component.scss']
})
export class PopUpAddNoteComponent implements OnInit {
  @Input() imei: string;
  /** user-scoped (Etap 16): gdy podany, notatki ida po pacjencie (dziala tez bez opaski); imei tylko dla starych wywolan */
  @Input() userId: number | null = null;
  @Input() modalType: number;

  noteForm: FormGroup = new FormGroup({
    description: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(1000)
    ])
  });

  descriptionHistory: string = '';
  notesList: NoteItem[] = [];
  selectedNoteIndex: number | null = null;
  isLoading: boolean = false;
  isSaving: boolean = false;
  deletingNote: NoteItem | null = null;

  /** Ile kart notatek udaje szkielet, zanim przyjdzie prawdziwa lista. */
  readonly skeletonNotes = [0, 1];

  /** Czy w tym otwarciu okna historia się zmieniła — ekran pod spodem pobiera ją wtedy
   *  na nowo. Okno jest nakładką, więc profil pacjenta zostaje pod nim zamontowany
   *  i sam z siebie nigdy by o nowej ani o usuniętej notatce nie wiedział. */
  private historyChanged = false;

  constructor(
    public activeModal: NgbActiveModal,
    private patientService: PatientService,
    private translate: TranslateService,
    private toast: ToastService,
    private confirmation: ConfirmationService,
    private modalService: ModalService,
    private userService: UserService
  ) {}

  /** Usuwanie notatek jest przywilejem managera - tak samo, jak reszta akcji zarządzania kontem. */
  get isManager(): boolean {
    return this.userService.getRole() === 3;
  }

  ngOnInit() {
    // Szkielet należy do OTWIERANIA okna. Odświeżenie listy po zapisie idzie tym samym
    // loadNote(), ale już bez chowania treści: podmiana obu kolumn na szkielet zwijała
    // okno do samego nagłówka i wyglądała jak drugie, obce okno.
    this.isLoading = true;
    this.loadNote();
  }

  get descriptionControl(): FormControl {
    return this.noteForm.get('description') as FormControl;
  }

  loadNote() {
    (this.userId ? this.patientService.getBandNoteByUser(this.userId) : this.patientService.getBandNote(this.imei)).subscribe({
      next: (data) => {
        this.descriptionHistory = '';
        this.notesList = [];

        if (data !== 'no-results' && data) {
          if (Array.isArray(data.notes)) {
            this.notesList = [...data.notes].reverse().map((note: any) => ({
              content: note.content || note.note || '',
              author: note.author || note.userName || 'Nieznany',
              date: this.formatDate(note.date || note.createdAt)
            }));
          } else if (data.note) {
            this.descriptionHistory = data.note;
            this.parseHistoryToList(data.note);
          }
        }
      },
      error: () => {
        this.descriptionHistory = '';
        this.notesList = [];
        // Strumień, który zbłądził, nie dochodzi do complete — bez tego szkielet
        // zostawał na ekranie na zawsze.
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  private parseHistoryToList(historyString: string) {
    if (!historyString) return;

    const entries = historyString.split(/\n\n+/).filter(entry => entry.trim());

    this.notesList = entries.map(entry => {
      const lines = entry.split('\n');
      const headerLine = lines[0] || '';
      const content = lines.slice(1).join('\n').trim() || '';

      const match = headerLine.match(/^(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2})\s*-\s*(.+)$/);

      let date = '';
      let author = 'Nieznany';
      let rawDate: string | undefined;

      if (match) {
        date = this.formatDate(match[1]);
        author = match[2].trim();
        rawDate = match[1];
      }

      return { content, author, date, rawDate };
    }).filter(note => note.content).reverse();
  }

  private formatDate(dateInput: string | Date): string {
    if (!dateInput) return '';

    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return String(dateInput);

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  trackByIndex(index: number) {
    return index;
  }

  selectNote(index: number) {
    this.selectedNoteIndex = this.selectedNoteIndex === index ? null : index;
  }

  saveNote() {
    this.noteForm.markAllAsTouched();

    if (this.noteForm.invalid) return;

    this.isSaving = true;
    (this.userId ? this.patientService.setBandNoteByUser(this.userId, this.descriptionControl.value) : this.patientService.setBandNote(this.imei, this.descriptionControl.value)).subscribe({
      next: () => {
        this.historyChanged = true;
        this.confirmation.showToast('DASHBOARD.SUCCESSFUL-ADD-NOTE');
        this.noteForm.reset();
        this.loadNote();
      },
      error: () => {
        this.isSaving = false;
        this.toast.showError(this.translate.instant('BACKEND.SOMETHING-WRONG'));
      },
      complete: () => {
        this.isSaving = false;
      }
    });
  }

  /** Usuwa jeden wpis notatki. Tylko manager - `isManager` steruje też widocznością przycisku w szablonie,
   *  ten warunek jest wyłącznie zabezpieczeniem na wypadek wywołania z pominięciem UI. */
  deleteNote(note: NoteItem) {
    if (!this.isManager || !note.rawDate || this.deletingNote !== null) return;

    this.modalService.openAccept(this.translate.instant('NOTES.DELETE_NOTE_CONFIRM'), 'EVENTS.SHARED.COMPONENTS.POP-UP-ADD-NOTE.POPUP.NOTES-DELETE_NOTE_CONFIRM')
      .pipe(take(1))
      .subscribe(result => {
        if (!result || !result.ok) return;

        this.deletingNote = note;
        this.patientService.deleteBandNote(this.imei, note.rawDate, note.author).subscribe({
          next: () => {
            this.historyChanged = true;
            if (this.selectedNoteIndex !== null) { this.selectedNoteIndex = null; }
            this.confirmation.showToast('NOTES.NOTE_DELETED');
            this.loadNote();
          },
          error: err => {
            this.deletingNote = null;
            this.toast.showError(this.translate.instant('BACKEND.SOMETHING-WRONG'));
            if (err?.errors === 'NOTE_NOT_FOUND') {
              this.historyChanged = true;
              this.loadNote();
            }
          },
          complete: () => {
            this.deletingNote = null;
          }
        });
      });
  }

  closeModal() {
    this.activeModal.close(this.historyChanged);
  }
}
